import * as React from "react";
import { Alert, Button, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import { legalLangFor } from "@/features/legal/docs";

/** Told once to each member when their org turns support chat on (spec
 *  §9.7, §11.3, Art. 13/14). The text is the provider's own
 *  ``member_notice`` block for the org's variant (sv or en, as the legal
 *  texts are): what is shared, that chat messages go to the provider and
 *  where the data is stored, the US in evaluation mode included. Until
 *  the member acknowledges it (or the hold ends) nothing about them is
 *  sent. Only the frame (buttons) is a UI string. */
export function AddonMemberNotice({
  slug,
  pending,
  notice,
}: {
  slug: string | null;
  pending: boolean;
  notice?: { sv: string; en: string };
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [done, setDone] = React.useState(false);
  const ack = useMutation({
    mutationFn: async () => (await api.post(`/orgs/${slug}/addons/support_chat/notice-ack`)).data,
    onSuccess: () => {
      setDone(true);
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
  if (!slug || !pending || done) return null;
  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <>
          <Button color="inherit" size="small" component={RouterLink} to="/app/account#privacy">
            {t("support.member_notice.object")}
          </Button>
          <Button color="inherit" size="small" onClick={() => ack.mutate()} disabled={ack.isPending}>
            {t("support.member_notice.ok")}
          </Button>
        </>
      }
    >
      {notice ? notice[legalLangFor(i18n.resolvedLanguage ?? i18n.language)] : t("support.member_notice.body")}
      {ack.isError && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {t("support.member_notice.error")}
        </Typography>
      )}
    </Alert>
  );
}
